/**
 * Shared API calls and hooks for template list (used by Data Scenarios sidebar and WYSIWYG Templates sidebar).
 */
import { useEffect, useState } from 'react';
import { RunContextApp } from 'framework/RCApp';
import { PilotApis } from 'apis/pilotAPI';
import { createCxApiClient } from 'framework/http/CxEditorClient';

const TOOL_API_URL = process.env.REACT_APP_TOOL_API_URL as string;

export interface ProjectAttributesForFuel {
  allowed_measurement_type?: string;
}

/**
 * Fetches project attributes (e.g. allowed_measurement_type) for the given pilot.
 * Used to derive available fuel types for template list.
 */
export async function fetchProjectAttributes(
  rc: RunContextApp,
  pilotId: string
): Promise<ProjectAttributesForFuel | null> {
  const response = await PilotApis.getProjectAttributes(rc, pilotId);
  if (!response.success || !response.data?.attributes) {
    return null;
  }
  const { allowed_measurement_type } = response.data.attributes;
  return { allowed_measurement_type };
}

/**
 * Fetches bulk-manage data for the given pilot (Web channel templates).
 * Returns a normalized array; empty array on failure or no data.
 */
export async function fetchBulkManageData(
  rc: RunContextApp,
  pilotId: string
): Promise<any[]> {
  const api = createCxApiClient(rc, TOOL_API_URL);
  const response = await api.getBulkManage(pilotId);
  const data = Array.isArray(response) ? response : (response?.data ?? response?.items ?? []);
  return Array.isArray(data) ? data : [];
}

/**
 * Hook: fetches bulk-manage data for the given pilot.
 * Shared by DataScenariosSidebar and WYSIWYG TemplatesSidebar.
 */
export function useBulkManageData(
  rc: RunContextApp,
  pilotId: string | null | undefined
): { data: any[] | null; loading: boolean; error: string | null } {
  const [data, setData] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!pilotId) {
      setData(null);
      setError(null);
      return;
    }

    let ignore = false;
    setLoading(true);
    setError(null);

    fetchBulkManageData(rc, String(pilotId))
      .then((arr) => {
        if (!ignore) {
          setData(arr);
        }
      })
      .catch((err: any) => {
        if (!ignore) {
          console.error('[useBulkManageData] Failed to fetch bulk-manage:', err);
          setError(err?.message ?? 'Failed to load Web templates');
          setData(null);
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [rc, pilotId]);

  return { data, loading, error };
}
